import { useEffect, useMemo, useRef, useState } from 'react';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Search, X } from '@/components/icons';
import { haystackWords, scoreItem, tokenize } from '../../lib/itemSearch';
import { ItemThumb } from '../ui/ItemThumb';

export interface PickerItem {
  id: number;
  code: string;
  name: string;
  category: string;
  currentStock: number;
  imageUrl?: string | null;
  stockStatus?: 'GREEN' | 'AMBER' | 'RED';
  barcode?: string | null;
  color?: string | null;
  size?: string | null;
  brand?: string | null;
  style?: string | null; // design / pattern
  collection?: string | null; // design line
}

interface Props {
  items: PickerItem[];
  value: number | '__custom__';
  onPick: (id: number) => void;
  onCustom: () => void;
  disabled?: boolean;
  className?: string;
  /** Show the item-master thumbnail (as a separate box beside the input, and in
   *  the dropdown rows) plus the selected item's SKU barcode. Opt-in so pickers
   *  whose items carry no image stay compact. */
  showImage?: boolean;
  /** Show the selected item's SKU barcode line under the input. Defaults to
   *  `showImage`; set false when the barcode has its own table column. */
  showBarcode?: boolean;
  /** Show the selected item's thumbnail beside the input. Defaults to true (when
   *  `showImage`); set false when the row already has its own image column. */
  showSelectedThumb?: boolean;
}

const UNCATEGORIZED = 'Uncategorized';

export function CategoryItemPicker({
  items,
  value,
  onPick,
  onCustom,
  disabled,
  className,
  showImage,
  showBarcode,
  showSelectedThumb,
}: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [flipUp, setFlipUp] = useState(false);
  const [wrapRect, setWrapRect] = useState<{ left: number; top: number; bottom: number; width: number } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const PANEL_MAX_H = 360;
  const PANEL_W = 340;

  // Compute panel viewport-anchor position (position: fixed) so any ancestor
  // overflow-hidden / overflow-auto container (like the STN items table)
  // cannot clip it. Also decide whether to flip above or below.
  useEffect(() => {
    if (!open) return;
    function recompute() {
      const el = wrapRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      setWrapRect({ left: rect.left, top: rect.top, bottom: rect.bottom, width: rect.width });
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;
      setFlipUp(spaceBelow < PANEL_MAX_H && spaceAbove > spaceBelow);
    }
    recompute();
    window.addEventListener('resize', recompute);
    window.addEventListener('scroll', recompute, true);
    return () => {
      window.removeEventListener('resize', recompute);
      window.removeEventListener('scroll', recompute, true);
    };
  }, [open, query]);

  const selected = useMemo(
    () => (typeof value === 'number' ? items.find((i) => i.id === value) : null),
    [items, value],
  );

  // Click-outside closes the picker.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery('');
      }
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Categories & their counts.
  const byCategory = useMemo(() => {
    const map = new Map<string, PickerItem[]>();
    for (const it of items) {
      const c = it.category?.trim() || UNCATEGORIZED;
      const arr = map.get(c) ?? [];
      arr.push(it);
      map.set(c, arr);
    }
    return Array.from(map.entries())
      .map(([name, list]) => ({ name, items: list.sort((a, b) => a.name.localeCompare(b.name)) }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [items]);

  // Type-ahead search — token + fuzzy across name/code/barcode/colour/size, so
  // "amelia/1580x230/grey" or a mistyped "artisian" still lands the right item.
  const filtered = useMemo(() => {
    const tokens = tokenize(query);
    if (!tokens.length) return null; // null => use category view
    const scored = items
      .map((it) => {
        const words = haystackWords([
          it.name,
          it.code,
          it.barcode,
          it.color,
          it.size,
          it.style,
          it.collection,
          it.brand,
          it.category,
        ]);
        const score = scoreItem(tokens, words);
        return score == null ? null : { it, score };
      })
      .filter((x): x is { it: PickerItem; score: number } => x !== null);
    scored.sort((a, b) => b.score - a.score || a.it.name.localeCompare(b.it.name));
    return scored.map((s) => s.it);
  }, [items, query]);

  function pickItem(it: PickerItem) {
    onPick(it.id);
    setOpen(false);
    setQuery('');
  }

  function pickCustom() {
    onCustom();
    setOpen(false);
    setQuery('');
  }

  const displayValue = selected
    ? `${selected.name}${selected.code ? ` (${selected.code})` : ''}`
    : value === '__custom__'
    ? '— Custom —'
    : '';

  return (
    <div ref={wrapRef} className={clsx('relative', className)}>
      <div className="flex items-center gap-1.5">
        {showImage && (showSelectedThumb ?? true) && selected && !open && <ItemThumb src={selected.imageUrl} size={30} />}
        <div className="relative min-w-0 flex-1">
          <input
            ref={inputRef}
            type="text"
            className="w-full rounded-md border border-[var(--border-color)] bg-[var(--input-bg)] px-2 py-1 pr-7 text-xs text-[var(--text-primary)] outline-none focus:border-accent disabled:opacity-60 sm:rounded-lg sm:px-3 sm:py-2 sm:pr-8 sm:text-sm"
            value={open ? query : displayValue}
            placeholder={selected ? '' : 'Search item or pick category…'}
            disabled={disabled}
            onFocus={() => setOpen(true)}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
          />
          {selected && !open && !disabled && (
            <button
              type="button"
              onClick={pickCustom}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-[var(--text-muted)] hover:text-[var(--text-primary)]"
              title="Clear selection"
              tabIndex={-1}
            >
              <X size={14} />
            </button>
          )}
        </div>
      </div>
      {(showBarcode ?? showImage) && selected && !open && selected.barcode && (
        <div className="mt-0.5 truncate text-[10px] text-[var(--text-muted)]">SKU: {selected.barcode}</div>
      )}

      {open && wrapRect && (
        <div
          className="fixed z-[500] max-w-[95vw] overflow-hidden rounded-lg border border-[var(--border-color)] bg-[var(--card-bg)] shadow-lg"
          style={{
            width: Math.max(PANEL_W, wrapRect.width),
            left: Math.min(wrapRect.left, window.innerWidth - PANEL_W - 8),
            top: flipUp
              ? Math.max(8, wrapRect.top - Math.min(PANEL_MAX_H, wrapRect.top - 8) - 4)
              : wrapRect.bottom + 4,
            maxHeight: Math.min(
              PANEL_MAX_H,
              flipUp ? wrapRect.top - 8 : window.innerHeight - wrapRect.bottom - 8,
            ),
          }}
          onMouseDown={(e) => e.stopPropagation()}
        >
          {/* Header row inside the panel */}
          <div className="flex items-center gap-1.5 border-b border-[var(--border-color)] bg-[var(--bg-soft)] px-3 py-2">
            <Search size={12} className="text-[var(--text-muted)]" />
            <span className="ml-auto text-[10px] font-bold uppercase tracking-wide text-[var(--text-muted)]">
              {filtered
                ? `${filtered.length} match${filtered.length === 1 ? '' : 'es'}`
                : `${items.length} item${items.length === 1 ? '' : 's'} · ${byCategory.length} categories`}
            </span>
          </div>

          <div className="max-h-[300px] overflow-y-auto">
            {/* Custom option is always available at the top */}
            {!filtered && (
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); pickCustom(); }}
                className={clsx(
                  'flex w-full items-center justify-between border-b border-[var(--border-color)] px-3 py-2 text-left text-sm',
                  value === '__custom__' ? 'bg-accent/10 text-accent' : 'hover:bg-[var(--input-bg)]',
                )}
              >
                <span className="italic">— Custom item —</span>
                <span className="text-[10px] text-[var(--text-muted)]">enter manually</span>
              </button>
            )}

            {/* SEARCH mode: flat filtered list across all categories */}
            {filtered && filtered.length === 0 && (
              <div className="px-3 py-3 text-xs text-[var(--text-muted)]">No items match "{query}"</div>
            )}
            {filtered && filtered.map((it) => (
              <ItemRow key={it.id} item={it} onPick={() => pickItem(it)} showImage={showImage} />
            ))}

            {/* GROUPED view: every item visible, grouped by category with
                sticky headers so long lists stay navigable. */}
            {!filtered && byCategory.length === 0 && (
              <div className="px-3 py-3 text-xs text-[var(--text-muted)]">No items yet.</div>
            )}
            {!filtered && byCategory.map((g) => {
              const isOpen = expanded.has(g.name);
              return (
                <div key={g.name}>
                  <button
                    type="button"
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      setExpanded((prev) => {
                        const next = new Set(prev);
                        if (next.has(g.name)) next.delete(g.name);
                        else next.add(g.name);
                        return next;
                      });
                    }}
                    className="sticky top-0 z-10 flex w-full items-center justify-between border-b border-[var(--border-color)] bg-[var(--bg-soft)] px-3 py-2 text-left text-[11px] font-extrabold uppercase tracking-wider text-accent hover:bg-accent/10"
                  >
                    <span className="flex items-center gap-1.5">
                      {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                      {g.name}
                    </span>
                    <span className="text-[var(--text-muted)]">{g.items.length}</span>
                  </button>
                  {isOpen && g.items.map((it) => (
                    <ItemRow key={it.id} item={it} onPick={() => pickItem(it)} showImage={showImage} />
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function ItemRow({ item, onPick, showImage }: { item: PickerItem; onPick: () => void; showImage?: boolean }) {
  const stockColor =
    item.stockStatus === 'RED' ? 'text-danger'
      : item.stockStatus === 'AMBER' ? 'text-warning'
      : 'text-success';
  return (
    <button
      type="button"
      onMouseDown={(e) => { e.preventDefault(); e.stopPropagation(); onPick(); }}
      className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm hover:bg-[var(--input-bg)]"
    >
      {showImage && <ItemThumb src={item.imageUrl} size={30} preview={false} />}
      <div className="min-w-0 flex-1">
        <div className="truncate font-medium text-[var(--text-primary)]">{item.name}</div>
        <div className="mt-0.5 truncate text-[10px] text-[var(--text-muted)]">
          {[item.code, item.barcode, item.category || UNCATEGORIZED].filter(Boolean).join(' · ')}
        </div>
      </div>
      <div className={clsx('flex-shrink-0 text-[10px] font-bold', stockColor)}>
        stock {item.currentStock}
      </div>
    </button>
  );
}
